import { env } from '@/config/env';

// Tenant used for the __tenant header before login (host/localhost dev). In
// production the tenant resolves from the subdomain, so this is a dev/host
// convenience matching the legacy "switch tenant" box on the login page.
const KEY = 'finkit.tenant';
// Sentinel recording an EXPLICIT "host / no tenant" choice, so clearing the
// switcher wins over the build-time env.tenant default (otherwise clearing and
// "never chose" are indistinguishable and host login resolves to env.tenant).
const HOST = '__host__';

export const tenantStore = {
  raw(): string | null {
    try {
      return localStorage.getItem(KEY);
    } catch {
      return null;
    }
  },
  get(): string {
    const r = this.raw();
    return r && r !== HOST ? r : '';
  },
  set(value: string): void {
    try {
      const v = value.trim();
      // Store the HOST sentinel for an explicit empty so it beats env.tenant.
      localStorage.setItem(KEY, v || HOST);
    } catch {
      /* ignore */
    }
    this.syncCookie();
  },

  // The SPA authenticates by the `__tenant` HEADER (sent on every request from
  // effective()), which ABP's tenant resolver prioritises over the cookie. The
  // backend still sets a long-lived `__tenant=<guid>` cookie on login and never
  // clears it on logout — a stale cookie then resolves the wrong tenant on the
  // next (esp. host) sign-in -> invalid_grant. So keep the cookie DELETED; the
  // header is authoritative. Called on app load, login-page mount, submit, logout.
  syncCookie(): void {
    try {
      document.cookie = '__tenant=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT';
    } catch {
      /* non-browser / disabled cookies */
    }
  },
  // Effective tenant used for the __tenant header. An explicit host choice
  // (HOST sentinel) → no tenant (host login). An explicit tenant → that tenant.
  // Only when NOTHING was ever chosen do we fall back to the build-time default.
  effective(): string {
    const r = this.raw();
    if (r === HOST) return '';
    if (r) return r;
    return env.tenant || '';
  },
  // Show the switcher during local development (incl. phone-over-LAN testing)
  // and on host/localhost origins. Hidden on real tenant subdomains in prod.
  isSwitchable(): boolean {
    if (import.meta.env.DEV) return true;
    const h = window.location.hostname;
    return (
      h === 'localhost' ||
      h === '127.0.0.1' ||
      h === '[::1]' ||
      /^192\.168\./.test(h) ||
      /^10\./.test(h) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(h)
    );
  },
};
